import moment from 'moment';
import { Tag } from 'antd';
import { connect } from 'react-redux';
import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { config } from '../../constant';
import cloneDeep from 'lodash.clonedeep';
import ReactEcharts from '../../Echarts/Echarts';
import { ErrorComponent, NoRecordComponent } from '../../Utils';
import { ArrayOfObjectsWithAction } from '../../../Mock/MockJson';
import { getChartDataWithoutSelection, getChartDataWithSelection } from '../../ChartCalls';

const PieBar = (props) => {
  const [option, setOption] = useState(getOption(props));
  const [refreshDateTime, setRefreshDateTime] = useState(moment());
  const [errorInAPI, setErrorInAPI] = useState(false);
  const [noDataInDB, setNoDataInDB] = useState(false);

  const [pieName, setPieName] = useState('');
  let timeTicket = useRef(null);
  let requestCheck = useRef(true);
  const abortController = useRef(new window.AbortController());

  const {
    graphprops,
    selectionDate,
    selectionMonth,
    DeviceCode,
    selectionDateRange,
    dayMonthYear,
    currentTimeZone,
  } = props;

  useEffect(() => {
    if (timeTicket.current) {
      clearInterval(timeTicket.current);
    }
    let refreshTime = graphprops?.refreshTime ? graphprops.refreshTime * 60000 : config.defaultRefreshTime;
    timeTicket.current = setInterval(() => {
      if (requestCheck.current) {
        getData();
      }
    }, refreshTime);
    return () => {
      abortController.current.abort();
      clearInterval(timeTicket.current);
    };
  }, []);

  useEffect(() => {
    getData(props);
  }, [
    selectionDate,
    selectionMonth,
    selectionDateRange,
    DeviceCode,
    dayMonthYear,
    props.translationCache
  ]);

  const getData = useCallback(async () => {
    requestCheck.current = false;
    setErrorInAPI(false);
    setNoDataInDB(false);
    let json = [];
    if (graphprops && graphprops.enableMock && graphprops.enableMock === true) {
      json = ArrayOfObjectsWithAction;
      requestCheck.current = true;
    } else {
      if (graphprops?.graph === 'rangeBar' && graphprops?.currentDateData !== undefined) {
        json = graphprops.currentDateData;
      } else {
        if (
          graphprops &&
          ((graphprops.DashboardLevelSelection === 'datePicker' ||
            graphprops.GraphLevelSelection === 'datePicker') &&
            selectionDate) ||
          (
            (graphprops.DashboardLevelSelection === 'monthPicker' ||
              graphprops.GraphLevelSelection === 'monthPicker') &&
            selectionMonth) ||
          (
            (graphprops.DashboardLevelSelection === 'dateRangePicker' ||
              graphprops.GraphLevelSelection === 'dateRangePicker') &&
            selectionDateRange) ||
          (
            (graphprops.DashboardLevelSelection === 'dateMonthYearPicker' ||
              graphprops.GraphLevelSelection === 'dateMonthYearPicker') &&
            dayMonthYear) ||
          (
            (graphprops.DashboardLevelSelection === 'deviceSelection' ||
              graphprops.GraphLevelSelection === 'deviceSelection') &&
            DeviceCode)
        ) {
          json = await getChartDataWithSelection({
            graphprops,
            selectionDate,
            selectionMonth,
            selectionDateRange,
            DeviceCode,
            dayMonthYear,
            abortController: abortController.current,
            currentTimeZone,
          });
          requestCheck.current = true;
        } else if (
          graphprops.GraphLevelSelection === 'none' ||
          graphprops.DashboardLevelSelection === 'none'
        ) {
          json = await getChartDataWithoutSelection({
            graphprops,
            abortController: abortController.current,
            DeviceCode,
            TimeZone: currentTimeZone,
          });
          requestCheck.current = true;
        }
      }
    }

    if ((json && json.message) || (Array.isArray(json) && json.length === 0 && graphprops.enableMock !== true)) {
      if (json.message) {
        setErrorInAPI(true);
      } else if (json.length === 0) {
        setNoDataInDB(true);
      }
    } else {
      if (json && Array.isArray(json)) {
        let line = [];
        let pie = json;
        if (pieName) {
          let lineData = json.filter((jsonData) => jsonData.Description === pieName);
          line = lineData && lineData[0] ? lineData[0].Action : [];
        } else {
          line = json && json[0] && json[0]['Action'] ? json[0]['Action'] : [];
        }
        let newOption = cloneDeep(getOption());
        newOption.yAxis = {
          ...newOption.yAxis,
          data: line.map((lineData) => ({
            name: lineData.Name,
            value: lineData.Value
          }))
        };
        newOption.series[0] = {
          ...newOption.series[0],
          data: line.map((lineData) => ({
            name: lineData.Name,
            value: lineData.Value
          }))
        };
        newOption.series[1] = {
          ...newOption.series[1],
          data: pie.map((pieData) => ({
            name: pieData.Description,
            value: pieData.Value
          }))
        };

        setOption(newOption);
        setRefreshDateTime(moment());
        setNoDataInDB(false);
      }
    }
    setErrorInAPI(errorInAPI);
    setNoDataInDB(noDataInDB);
  }, [props, pieName]);

  const onChartClick = (cellData) => {
    if (cellData.componentSubType === 'pie') {
      setPieName(cellData.name);
      getData();
    }
  };

  const onEvents = {
    click: (cellData) => onChartClick(cellData)
  };

  return (
    <Fragment>
      {props.graphprops && props.graphprops.checked === true ? (
        <Tag className="graphTag" color="blue">
          {refreshDateTime ? moment(refreshDateTime).format('YYYY-MM-DD HH:mm:ss') : ''}
        </Tag>
      ) : null}
      {noDataInDB || errorInAPI ? (
        noDataInDB ? (
          <NoRecordComponent />
        ) : (
          <ErrorComponent />
        )
      ) : (
        <ReactEcharts
          option={option}
          notMerge={true}
          lazyUpdate={true}
          onEvents={onEvents}
          style={{ height: '90%', width: '100%' }}
          className="react_for_echarts"
        />
      )}
    </Fragment>
  );

  function getOption() {
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b} : {c} ({d}%)'
      },
      label: {
        color: props.theme.algorithm === "dark" ? 'white' : 'black',
        fontWeight: 'bold',
        fontSize: 14
      },
      textStyle: {
        color: props.theme.algorithm === "dark" ? 'white' : 'black',
        fontWeight: 'bold',
        fontSize: 18
      },
      legend: {
        left: 'center',
        textStyle: {
          color: props.theme.algorithm === "dark" ? 'white' : 'black'
        }
      },
      toolbox: {
        showTitle: false,
        itemSize: 25,
        feature: {
          saveAsImage: {
            title: 'download',
            name: props.graphprops.title,
            backgroundColor: 'black',
            iconStyle: {
              borderColor: '#8c8c8c',
              borderWidth: 2
            }
          }
        }
      },
      grid: [
        {
          top: 50,
          width: '50%',
          bottom: '45%',
          left: 10,
          containLabel: true
        },
        {
          top: '55%',
          width: '50%',
          bottom: 0,
          left: 10,
          containLabel: true
        }
      ],
      xAxis: [
        {
          type: 'value',
          axisLine: {
            lineStyle: {
              color: props.theme.algorithm === "dark" ? 'white' : 'black'
            }
          },
          axisTick: {
            lineStyle: {
              color: props.theme.algorithm === "dark" ? 'white' : 'black'
            }
          },
          axisLabel: {
            color: props.theme.algorithm === "dark" ? 'white' : 'black'
          },
          splitLine: {
            show: false
          }
        }
      ],
      yAxis: [
        {
          type: 'value',
          data: [],
          axisLine: {
            lineStyle: {
              color: props.theme.algorithm === "dark" ? 'white' : 'black'
            }
          },
          axisTick: {
            lineStyle: {
              color: props.theme.algorithm === "dark" ? 'white' : 'black'
            }
          },
          gridIndex: 0,
          axisLabel: {
            interval: 0,
            rotate: 30,
            color: props.theme.algorithm === "dark" ? 'white' : 'black'
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          type: 'bar',
          stack: 'chart',
          z: 3,
          label: {
            position: 'right',
            show: true
          },
          data: []
        },
        {
          type: 'pie',
          radius: [0, '40%'],
          center: ['60%', '50%'],
          data: []
        }
      ]
    };
  }
};

const mapStateToProps = (state) => {
  return {
    theme: state.ThemeReducer,
    translationCache: state.TranslationReducer.translationCache,
    currentTimeZone: state.LayoutReducer.currentTimeZone
  };
};
export default connect(mapStateToProps)(PieBar);